import React, { useState, useMemo } from 'react';
import './GoanFiesta.css';
import { paginateMenu } from '../../../utils/menuPagination';
import { exportPtPagesToPdf } from '../../../utils/exportPtPagesToPdf';
import ExportPdfFab from '../../../components/ExportPdfFab';

const GoanFiesta = ({ data, isSingleMenu }) => {
    const restaurant = data?.restaurant || {};
    const rawMenus = data?.menus || [];

    const [activeMenuIndex, setActiveMenuIndex] = useState(0);

    // Determine which pages to display
    const visiblePages = useMemo(() => {
        if (isSingleMenu) {
            return rawMenus.flatMap(menu => paginateMenu(menu));
        }
        const activeMenu = rawMenus[activeMenuIndex];
        return activeMenu ? paginateMenu(activeMenu) : [];
    }, [isSingleMenu, rawMenus, activeMenuIndex]);

    const handleNext = () => {
        if (activeMenuIndex < rawMenus.length - 1) {
            setActiveMenuIndex(p => p + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePrev = () => {
        if (activeMenuIndex > 0) {
            setActiveMenuIndex(p => p - 1);
            window.scrollTo(0, 0);
        }
    };

    if (!data) return <div className="loading">Loading...</div>;

    return (
        <div className="gf-root">
            {/* Navigation - Only show in Multi-Menu Mode (Preview) */}
            {!isSingleMenu && (
                <div className="tmpl-nav-container no-print" style={{ justifyContent: 'center' }}>
                    <div className="tmpl-nav-controls">
                        <button className="tmpl-nav-btn" onClick={handlePrev} disabled={activeMenuIndex === 0}>
                            ← Prev Menu
                        </button>
                        <div className="tmpl-page-indicator">
                            {rawMenus[activeMenuIndex]?.name || `Menu ${activeMenuIndex + 1}`}
                            <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>
                                ({activeMenuIndex + 1}/{rawMenus.length})
                            </span>
                        </div>
                        <button className="tmpl-nav-btn" onClick={handleNext} disabled={activeMenuIndex === rawMenus.length - 1}>
                            Next Menu →
                        </button>
                    </div>
                </div>
            )}

            <ExportPdfFab onClick={() => exportPtPagesToPdf({ filename: `${restaurant.name || 'menu'}-goan-fiesta.pdf` })} />

            <div className="gf-menu-container">
                {visiblePages.map((page, index) => {
                    const categories = (page.categories || []);

                    // Header logic:
                    // PDF: Only on very first page
                    // Preview: On first page of the active menu
                    const showHeader = isSingleMenu ? (index === 0) : (page.chunkIndex === 0);

                    // Menu Title Logic: Only show on first page of the menu chunk
                    const showMenuTitle = page.chunkIndex === 0;

                    return (
                        <article key={`${page.id || page.name}-${index}`} className="pt-page active">
                            <div className="gf-border-decoration"></div>
                            {showHeader && (
                                <header className="gf-header">
                                    <div className="gf-sun-icon">☀️</div>
                                    <h1 className="gf-name">{restaurant.name}</h1>
                                    {restaurant.tagline && <p className="gf-tagline">{restaurant.tagline}</p>}
                                    <div className="gf-contact">
                                        {restaurant.address && <span>{restaurant.address}</span>}
                                        {restaurant.phoneNumber && <span> • {restaurant.phoneNumber}</span>}
                                    </div>
                                </header>
                            )}

                            <main className="gf-menu">
                                {showMenuTitle && (
                                    <h2 className="gf-menu-type">{page.name}</h2>
                                )}
                                <div className="gf-categories">
                                    {categories.map(category => (
                                        <section key={category.id} className="gf-category">
                                            <h3 className="gf-category-title">{category.name}</h3>
                                            <div className="gf-items">
                                                {(category.items || []).map(item => (
                                                    <div key={item.id} className="gf-item">
                                                        <div className="gf-item-header">
                                                            <div className="gf-item-name-group">
                                                                {(item.isVeg !== undefined && item.isVeg !== null) && (
                                                                    <span className={`gf-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                                                                        <span className="gf-veg-mark"></span>
                                                                    </span>
                                                                )}
                                                                <span className="gf-item-name">{item.name}</span>
                                                            </div>
                                                            <span className="gf-item-dots"></span>
                                                            <span className="gf-item-price">₹{item.price}</span>
                                                        </div>
                                                        {item.description && <p className="gf-item-description">{item.description}</p>}
                                                    </div>
                                                ))}
                                            </div>
                                        </section>
                                    ))}
                                </div>
                            </main>
                            <footer className="gf-footer">
                                <div className="gf-palm-leaf">🌴</div>
                                {restaurant.footerText && <p>{restaurant.footerText}</p>}
                                <div className="gf-palm-leaf">🌴</div>
                            </footer>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

export default GoanFiesta;
